import { assignInlineVars } from '@vanilla-extract/dynamic'
import classNames from 'classnames'
import { z } from 'zod'
import * as styles from './progress-bar.css'

const MIN_VALUE = 0
const MAX_VALUE = 1

export const ProgressBarSizeSchema = z.enum(['small', 'medium'])
export type ProgressBarSize = z.infer<typeof ProgressBarSizeSchema>

export const ProgressBarRadiusSchema = z.enum(['partial', 'full'])
export type ProgressBarRadius = z.infer<typeof ProgressBarRadiusSchema>

export const ProgressBarTrackColorSchema = z.enum(['default', 'alternate'])
export type ProgressBarTrackColor = z.infer<typeof ProgressBarTrackColorSchema>

export const ProgressBarFillSchema = z.enum(['solid', 'dynamic', 'gradient'])
export type ProgressBarFill = z.infer<typeof ProgressBarFillSchema>

export type ProgressBarProps = Pick<React.ComponentPropsWithRef<'div'>, 'ref' | 'aria-label'> & {
  size?: ProgressBarSize
  radius?: ProgressBarRadius
  trackColor?: ProgressBarTrackColor
  value?: number
  fill?: ProgressBarFill
}

export const ProgressBar: React.FC<ProgressBarProps> = ({
  ref,
  size = 'small',
  radius = 'partial',
  trackColor = 'default',
  value,
  fill = 'dynamic',
  'aria-label': ariaLabel,
}) => {
  const clampedValue = value !== undefined ? Math.max(MIN_VALUE, Math.min(MAX_VALUE, value)) : undefined

  return (
    <div
      ref={ref}
      role="progressbar"
      aria-label={ariaLabel}
      aria-valuemin={MIN_VALUE}
      aria-valuemax={MAX_VALUE}
      aria-valuenow={clampedValue}
      className={classNames(
        styles.track,
        styles.trackSize[size],
        styles.trackRadius[radius],
        styles.trackColor[trackColor]
      )}
      style={assignInlineVars({ [styles.progressValue]: String(clampedValue ?? MIN_VALUE) })}
    >
      <div className={styles.fill({ fill })} />
    </div>
  )
}
